import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Play, ExternalLink, Star } from 'lucide-react';
import { portfolioItems } from '../mock';

const Portfolio = () => {
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <section id="portfolio" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-wide">
            Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Nos réalisations récentes
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez quelques projets que nous avons eu le plaisir de produire
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {portfolioItems.map((item) => (
            <Card
              key={item.id}
              className="group overflow-hidden border-2 border-gray-100 hover:border-orange-500 transition-all duration-300 cursor-pointer"
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.project}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/90 via-orange-900/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
                
                {/* Play Button Overlay */}
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                    hoveredId === item.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="bg-orange-600 rounded-full p-4 shadow-lg hover:bg-orange-700 transition-colors duration-200">
                    <Play className="w-8 h-8 text-white fill-current" />
                  </div>
                </div>

                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <Badge className="bg-orange-600 text-white">
                    {item.year}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {item.project}
                    </h3>
                    <p className="text-orange-600 font-medium text-sm mb-2">
                      {item.artist}
                    </p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-gray-600 border-gray-300">
                    {item.genre}
                  </Badge>
                  <span className="text-sm text-gray-600">{item.description}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Testimonials Section */}
        <div className="mt-24">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Ce que disent nos clients
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Thomas Martin",
                role: "Artiste Indépendant",
                content: "Une expérience exceptionnelle ! L'équipe est professionnelle et à l'écoute. Le son obtenu dépasse mes attentes.",
                rating: 5
              },
              {
                name: "Sophie Leroux",
                role: "Productrice",
                content: "Studio au top avec du matériel de qualité. L'ingénieur du son est d'un grand talent. Je recommande vivement !",
                rating: 5
              },
              {
                name: "Alex Dubois",
                role: "Musicien",
                content: "Ambiance créative et résultats professionnels. C'est devenu mon studio de référence pour tous mes projets.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <Card key={index} className="bg-orange-50 border-orange-100">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
